import React, { useState, useEffect } from 'react';
import createAxiosInstance from '../../utils/axiosConfig';
import { FrameworkDomain } from '../../utils/interfaces';
import { create_question_flow } from '../../utils/endpoints';
import Alert from './alerts/alert';

interface AddQuestionFlowSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedDomain: FrameworkDomain | null;
  domains: FrameworkDomain[];
}

interface QuestionFlowFormData {
  domain_id: number;
  title: string;
  description: string;
  is_enabled: boolean;
  overall_score: number;
  is_deleted: boolean;
}

const AddQuestionFlowSidebar: React.FC<AddQuestionFlowSidebarProps> = ({
  isOpen,
  onClose,
  onSuccess,
  selectedDomain,
  domains
}) => {
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'danger' | 'warning'; show: boolean } | null>(null);
  const [formData, setFormData] = useState<QuestionFlowFormData>({
    domain_id: 0,
    title: '',
    description: '',
    is_enabled: true,
    overall_score: 0,
    is_deleted: false
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Set domain_id when selectedDomain changes
  useEffect(() => {
    if (selectedDomain) {
      setFormData(prev => ({ ...prev, domain_id: selectedDomain.id }));
    }
  }, [selectedDomain]);

  // Form validation
  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.domain_id) {
      newErrors.domain_id = 'Please select a domain';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Shortcode is required';
    }
    if (formData.overall_score < 0 || formData.overall_score > 100) {
      newErrors.overall_score = 'Overall score must be between 0 and 100';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (name === 'is_deleted' ? !checked : checked): 
              (name === 'domain_id' ? parseInt(value) : 
               name === 'overall_score' ? parseFloat(value) || 0 : value)
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Add question flow
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    
    setLoading(true);
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(create_question_flow, formData);
      
      if (response.status === 200 || response.status === 201) {
        setAlert({
          message: 'Question flow added successfully!',
          type: 'success',
          show: true
        });
        
        // Reset form
        setFormData({
          domain_id: selectedDomain?.id || 0,
          title: '',
          description: '',
          is_enabled: true,
          overall_score: 0,
          is_deleted: false
        });
        
        onSuccess();
        
        // Close sidebar after a short delay
        setTimeout(() => {
          handleClose();
        }, 1500);
      }
    } catch (error) {
      console.error('Error adding question flow:', error);
      setAlert({
        message: 'Error adding question flow. Please try again.',
        type: 'danger',
        show: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      domain_id: selectedDomain?.id || 0,
      title: '',
      description: '',
      is_enabled: true,
      overall_score: 0,
      is_deleted: false
    });
    setErrors({});
    setLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="offcanvas-backdrop fade show"
        onClick={handleClose}
      ></div>
      <div
        className="offcanvas offcanvas-end show"
        tabIndex={-1}
        style={{ 
          zIndex: 1050, 
          width: '40vw',
          maxWidth: '500px'
        }}
      >
        <div className="offcanvas-header" style={{backgroundColor: 'white', borderBottom: '1px solid #dee2e6'}}>
          <h5 className="offcanvas-title" style={{
            fontSize: '1.25rem',
            fontWeight: 'bold',
            color: '#183FCE'
          }}>
            Add New Question Flow
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={handleClose}
          ></button>
        </div>
        
        <div className="offcanvas-body">
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="flowDomain" className="form-label">Select Domain:</label>
              <select
                className={`form-select bg-white ${errors.domain_id ? 'is-invalid' : ''}`}
                id="flowDomain"
                name="domain_id"
                value={formData.domain_id}
                onChange={handleChange}
                disabled={loading}
              >
                <option value={0}>Select a domain</option>
                {domains.map((domain) => (
                  <option key={domain.id} value={domain.id}>{domain.title}</option>
                ))}
              </select>
              {errors.domain_id && (
                <div className="invalid-feedback">{errors.domain_id}</div>
              )}
            </div>
            
            <div className="mb-3">
              <label htmlFor="flowTitle" className="form-label">Title:</label>
              <input
                type="text"
                className={`form-control bg-white ${errors.title ? 'is-invalid' : ''}`}
                id="flowTitle"
                name="title"
                value={formData.title}
                onChange={handleChange}
                disabled={loading}
              />
              {errors.title && (
                <div className="invalid-feedback">{errors.title}</div>
              )}
            </div>
            
            <div className="mb-3">
              <label htmlFor="flowDescription" className="form-label">Shortcode:</label>
              <input
                type="text"
                className={`form-control bg-white ${errors.description ? 'is-invalid' : ''}`}
                id="flowDescription"
                name="description"
                value={formData.description}
                onChange={handleChange}
                disabled={loading}
              />
              {errors.description && (
                <div className="invalid-feedback d-block text-danger" style={{fontSize: '1rem'}}>{errors.description}</div>
              )}
            </div>
            
            <div className="mb-3">
              <label htmlFor="overallScore" className="form-label">Overall Score:</label>
              <input
                type="number"
                className={`form-control bg-white ${errors.overall_score ? 'is-invalid' : ''}`}
                id="overallScore"
                name="overall_score"
                value={formData.overall_score}
                onChange={handleChange}
                disabled={loading}
                min="0"
                max="100"
                step="0.01"
                placeholder="0.00"
              />
              {errors.overall_score && (
                <div className="invalid-feedback">{errors.overall_score}</div>
              )}
              <small className="text-muted">Enter a score between 0 and 100</small>
            </div>
            
            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="flowEnabled"
                  name="is_enabled"
                  checked={formData.is_enabled}
                  onChange={handleChange}
                  disabled={loading}
                />
                <label className="form-check-label" htmlFor="flowEnabled">
                  Enable Question Flow
                </label>
              </div>
            </div>
            
            <div className="mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="flowDeleted"
                  name="is_deleted"
                  checked={!formData.is_deleted}
                  onChange={handleChange}
                  disabled={loading}
                />
                <label className="form-check-label" htmlFor="flowDeleted">
                  Mark as Active
                </label>
              </div>
            </div>
            
            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-rounded"
                disabled={loading || domains.length === 0}
              >
                {loading ? (
                  <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    Adding...
                  </div>
                ) : (
                  'Add Question Flow'
                )}
              </button>
            </div>
            
            {domains.length === 0 && (
              <div className="alert alert-warning mt-3">
                Please add at least one domain before creating question flows.
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Alert */}
      {alert && (
        <Alert
          message={alert.message}
          type={alert.type}
          show={alert.show}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default AddQuestionFlowSidebar; 